import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useLang } from "../i18n";

const HERO_VIDEO = "https://videos.pexels.com/video-files/1851190/1851190-hd_1920_1080_25fps.mp4";
// The video's own still (the globe) — so the first frame shown matches the video,
// instead of a mismatched bokeh image flashing before it plays.
const HERO_POSTER = "https://images.pexels.com/videos/1851190/free-video-1851190.jpg";

const PARTICLES = Array.from({ length: 26 }).map((_, i) => ({
  left: `${(i * 37) % 100}%`,
  size: 1 + ((i * 7) % 4),
  delay: (i % 10) * 0.9,
  duration: 7 + ((i * 3) % 8),
}));

export default function Hero() {
  const { t } = useLang();
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  // Best-effort autoplay: some mobile browsers ignore the autoPlay attribute, so
  // kick playback off on mount. (iOS Low Power Mode still blocks it — nothing can
  // override that — but the poster now shows the globe, not a stray bokeh image.)
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const played = v.play?.();
    if (played && typeof played.catch === "function") played.catch(() => {});
  }, []);

  const fade = {
    hidden: { opacity: 0, y: 30 },
    show: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: 0.4 + i * 0.18, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
    }),
  };

  return (
    <section id="top" className="relative flex min-h-screen items-center overflow-hidden">
      {/* Background video */}
      <video
        ref={videoRef}
        className="absolute inset-0 h-full w-full object-cover"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        poster={HERO_POSTER}
        onPlaying={() => setPlaying(true)}
      >
        <source src={HERO_VIDEO} type="video/mp4" />
      </video>

      {/* Static globe still that covers the video — and any iOS Low-Power-Mode play
          button — until the video actually starts playing. */}
      <img
        src={HERO_POSTER}
        alt=""
        aria-hidden="true"
        className={`pointer-events-none absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
          playing ? "opacity-0" : "opacity-100"
        }`}
      />

      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-ink/85 via-ink/55 to-ink" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/40 to-transparent" />
      <div className="absolute inset-0 bg-[radial-gradient(120%_90%_at_50%_50%,transparent_45%,rgba(5,5,5,0.6)_100%)]" />

      {/* Soft gold glow behind the headline */}
      <div className="pointer-events-none absolute left-[6%] top-1/2 h-[46vh] w-[46vh] -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.12),transparent_70%)] blur-2xl" />

      {/* Floating gold particles */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {PARTICLES.map((p, i) => (
          <span
            key={i}
            className="absolute bottom-0 rounded-full bg-gold/70"
            style={{
              left: p.left,
              width: `${p.size}px`,
              height: `${p.size}px`,
              animation: `floatUp ${p.duration}s ${p.delay}s ease-in infinite`,
            }}
          />
        ))}
      </div>

      <div className="container-luxe relative z-10 w-full">
        <h1 className="font-serif font-light leading-[0.9] text-white">
          <motion.span
            custom={0}
            variants={fade}
            initial="hidden"
            animate="show"
            className="block text-5xl sm:text-6xl lg:text-[7.5rem]"
          >
            {t.hero.title1}
          </motion.span>
          <motion.span
            custom={1}
            variants={fade}
            initial="hidden"
            animate="show"
            className="block bg-gradient-to-r from-[#f3dd95] via-[#d4af37] to-[#9c7322] bg-clip-text text-5xl italic text-transparent sm:text-6xl lg:text-[7.5rem]"
          >
            {t.hero.title2}
          </motion.span>
        </h1>

        <motion.div
          custom={2}
          variants={fade}
          initial="hidden"
          animate="show"
          className="mt-9 flex max-w-xl items-start gap-5"
        >
          <span className="mt-2 hidden h-16 w-px shrink-0 bg-gradient-to-b from-gold/70 to-transparent sm:block" />
          <p className="text-base leading-relaxed text-white/70 md:text-lg">{t.hero.subtitle}</p>
        </motion.div>

        <motion.div
          custom={3}
          variants={fade}
          initial="hidden"
          animate="show"
          className="mt-12 flex flex-wrap items-center justify-start gap-x-5 gap-y-3 sm:gap-x-7"
        >
          {t.hero.meta.map((m, i) => (
            <React.Fragment key={m}>
              {i > 0 && <span className={`h-3.5 w-px bg-gold/35 ${i === 2 ? "hidden sm:block" : ""}`} />}
              <span
                className={`whitespace-nowrap text-[11px] uppercase tracking-[0.28em] ${
                  i === 0 ? "text-gold-leaf" : "text-white/55"
                }`}
              >
                {m}
              </span>
            </React.Fragment>
          ))}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/50">{t.hero.scroll}</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ChevronDown className="text-gold" size={20} />
        </motion.div>
      </motion.div>
    </section>
  );
}
